import {
  TDashboardEditComponent,
  TDashboardEditComponentProps,
} from "MyDashboards/components/ComponentRegistry/types";
import useUploadSlideImage from "MyDashboards/hooks/useUploadSlideImage";
import useDashboardEditor from "MyDashboards/state/editor";
import {
  base64toBlob,
  calculateAspectRatioFit,
  getImgDimensions,
  toBase64,
} from "MyDashboards/utils/utils";
import FileUploadInput from "qpr-base/ui/components/FileUploadInput";
import QPRButton from "qpr-base/uicomponents/QPRButton";
import { getFileApiURL as fileURLToPath } from "qpr-base/utils/url-utils";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Else, If, Then } from "react-if";
import Resizer from "react-image-file-resizer";
import useImageEditorEditStyles from "./themes";

import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import sha256 from "MyDashboards/utils/sha256";
import uploadFile from "MyDashboards/utils/uploadFile";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import ReactCrop, { Crop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const uploadImage = async (base64: string): Promise<string> => {
  const start = `data:image/png;base64,`;
  const base64ImageData = base64.slice(start.length);
  const sha = await sha256(base64ImageData);
  const ref = `${sha}.png`;
  try {
    await uploadFile({
      hashFileName: ref,
      fileContent: base64ImageData,
    });
    return ref;
  } catch (e) {
    return "";
  }
};

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const ImageEditorEdit: TDashboardEditComponent = (
  props: TDashboardEditComponentProps
) => {
  const classes = useImageEditorEditStyles();
  const setElementProperties = useDashboardEditor(
    (s) => s.setSelectedElementProperties
  );
  const files = useRef<File[]>([]);
  const { t } = useTranslation();
  const ctx = useAppContext();
  const {
    uploadSlideImage,
    isLoading: isUploadLoading,
    isError: isUploadError,
    data: uploadedFileRef,
  } = useUploadSlideImage();

  const [aspect, setAspect] = React.useState(props.el.width / props.el.height);
  const [crop, setCrop] = React.useState<Crop>();
  const [newImageUrl, setNewImageUrl] = React.useState<string>("");
  const imgRef = useRef<HTMLImageElement>(null);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;

      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  const webSlideToPPTRatio = 2;
  /**
   * fits image to element width and height. if it is already fit, nothing happens and image is returned as is
   */
  const getDownScaledImg = async (base64: string): Promise<string> => {
    const element = props.el;
    const { width: imgWidth, height: imgHeight } = await getImgDimensions(
      base64
    );

    if (imgWidth <= element.width && imgHeight <= element.height) return base64;

    const newDimensions = calculateAspectRatioFit(
      imgWidth,
      imgHeight,
      element.width,
      element.height
    );
    console.log("original image dimensions", imgWidth, "x", imgHeight);
    console.log(
      "Downscaled dimensions",
      newDimensions.width,
      "x",
      newDimensions.height
    );

    const blob = await base64toBlob(base64);
    /// Don't depend on this function to calculate the resize, just use it to do ACTUAL resize
    return new Promise<string>((resolve, err) =>
      Resizer.imageFileResizer(
        blob, // Is the file of the image which will resized.
        newDimensions.width * webSlideToPPTRatio, // Is the maxWidth of the resized new image.
        newDimensions.height * webSlideToPPTRatio, // Is the maxHeight of the resized new image.
        "PNG", // Is the compressFormat of the resized new image.
        100, // Is the quality of the resized new image.
        0, // Is the degree of clockwise rotation to apply to uploaded image.
        (res: any) => {
          resolve(res as string);
        }, // Is the callBack function of the resized new image URI.
        "base64", // Is the output type of the resized new image.
        newDimensions.width * webSlideToPPTRatio, // Is the minWidth of the resized new image.
        newDimensions.height * webSlideToPPTRatio // Is the minHeight of the resized new image.
      )
    );
  };

  const applyImg = async (file: File) => {
    const base64 = await toBase64(file);
    const downScaledImgb64 = await getDownScaledImg(base64);
    const { width, height } = await getImgDimensions(downScaledImgb64);
    const imgName = (await uploadSlideImage(downScaledImgb64))?.ref;
    let elWidth = width / webSlideToPPTRatio;
    let elHeight = height / webSlideToPPTRatio;
    if (width > 900 || height > 500) {
      elWidth = width / 2;
      elHeight = height / 2;
    }
    console.log("element dimensions", elWidth, "x", elHeight);

    setElementProperties({
      width: elWidth,
      height: elHeight,
      rendered: {
        type: "image",
        pngRef: imgName as string,
      },
    });
  };

  const cropImageNow = async (crop: Crop, image: HTMLImageElement) => {
    const { el } = props;

    let ratio = image.width / image.naturalWidth;

    const cropPosition = {
      x:
        crop.unit === "%"
          ? (crop.x * image.naturalWidth) / 100
          : crop.x / ratio,
      y:
        crop.unit === "%"
          ? (crop.y * image.naturalHeight) / 100
          : crop.y / ratio,
      width:
        crop.unit === "%"
          ? (crop.width * image.naturalWidth) / 100
          : crop.width / ratio,
      height:
        crop.unit === "%"
          ? (crop.height * image.naturalHeight) / 100
          : crop.height / ratio,
    };

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (ctx) {
      canvas.width = el.width;
      canvas.height = el.height;
      ctx.drawImage(
        image,
        cropPosition.x,
        cropPosition.y,
        cropPosition.width,
        cropPosition.height,
        0,
        0,
        el.width,
        el.height
      );
      const base64Image = canvas.toDataURL("image/png");

      console.log("Crop", base64Image);

      const imgName = await uploadImage(base64Image);

      /*
      let elWidth = width / webSlideToPPTRatio;
      let elHeight = height / webSlideToPPTRatio;
      if (width > 900 || height > 500) {
        elWidth = width / 2;
        elHeight = height / 2;
      }
      console.log('element dimensions', elWidth, 'x', elHeight);
      */
      setElementProperties({
        rendered: {
          type: "image",
          pngRef: imgName as string,
        },
      });
    }

    // for example image natural size would be
    // Image size:   1280 720
    // Element size: 900 506.25

    /*
    const canvas = document.createElement('canvas');

    const scale = image.naturalWidth / el.width;
    
    const scaleX = image.naturalWidth / el.width;
    const scaleY = image.naturalHeight / el.width;

    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    // Converting to base64
    const base64Image = canvas.toDataURL('image/png');
    // setOutput(base64Image);

    const downScaledImgb64 = await getDownScaledImg(base64Image);
    const { width, height } = await getImgDimensions(downScaledImgb64);
    const imgName = (await uploadSlideImage(downScaledImgb64))?.ref;
    let elWidth = width / webSlideToPPTRatio;
    let elHeight = height / webSlideToPPTRatio;
    if (width > 900 || height > 500) {
      elWidth = width / 2;
      elHeight = height / 2;
    }
    console.log('element dimensions', elWidth, 'x', elHeight);

    setElementProperties({
      width: elWidth,
      height: elHeight,
      rendered: {
        type: 'image',
        pngRef: imgName as string
      }
    });
    */
  };

  const removeImgFromComponent = () => {
    setElementProperties({
      rendered: {
        type: "image",
        pngRef: undefined,
      },
    });
  };

  return (
    <div className={classes.container}>
      <If condition={props.el.rendered?.pngRef}>
        <Then>
          <img
            className="curImg"
            src={fileURLToPath(`/cache/${props.el.rendered?.pngRef}`)}
            alt="Slide-data"
          />
          <QPRButton
            type="button"
            onClick={removeImgFromComponent}
            className="removeBtn"
          >
            {t("myStories.Remove image")}
          </QPRButton>
        </Then>
        <Else>
          {newImageUrl ? (
            <div
              style={{
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div>
                <Button
                  color="primary"
                  onClick={() => {
                    if (crop && imgRef.current) {
                      cropImageNow(crop, imgRef.current);
                    }
                  }}
                >
                  {t("buttons.Save")}
                </Button>
                <Button
                  color="secondary"
                  onClick={() => {
                    setNewImageUrl("");
                  }}
                >
                  {t("buttons.Cancel")}
                </Button>
              </div>
              <ReactCrop
                crop={crop}
                onChange={(c: any) => {
                  setCrop(c);
                }}
                onComplete={(c: any) => {
                  // setCompletedCrop(c)
                }}
                aspect={aspect}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={newImageUrl}
                  // style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </div>
          ) : (
            <FileUploadInput
              onFiles={(f: File[]) => {
                const [file] = f;
                if (file) {
                  setCrop(undefined); // Makes crop preview update between images.
                  const reader = new FileReader();
                  reader.addEventListener("load", () => {
                    const imageUrl = reader.result?.toString() || "";
                    if (imageUrl) {
                      // setNewImageUrl
                      setNewImageUrl(imageUrl);
                    }
                  });
                  reader.readAsDataURL(file);
                }

                // Original code here:
                /*
              files.current = f;
              if (f.length > 0) applyImg(f[0]);
              */
              }}
              accept={[".png", ".webp", ".jpg", ".jpeg"]}
              maxFiles={1}
            />
          )}
        </Else>
      </If>

      {ctx.page === "editslidetemplatedashboard" ? (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Switch
            checked={props.el.isStatic}
            onChange={(ev: any, checked: boolean) => {
              setElementProperties({
                isStatic: checked,
              });
            }}
          />
          <div>{props.el.isStatic ? t("myStories.Static") : t("myStories.Movable")}</div>
        </div>
      ) : null}
    </div>
  );
};

export default ImageEditorEdit;
